import React, { useState, useEffect } from 'react'
import { BrowserRouter as Router, Routes, Route, Link, useNavigate } from 'react-router-dom'
import mclovin from './Imatges/mclovin.jpg'
import logo from './Imatges/logoUsuari.jpg'
import countryCodes from './countryCodes.json'
import NewVideo from './Video/videoeditat.mp4'
import './App.css'
import Compra from './pages/Compra.js'
import Alquilar from './pages/Alquilar.js'
import SearchBar from './Components/searchBar'
import Login from './pages/Login.js'
import VehicleDetail from './pages/VehicleDetail.js'
import AddVehicle from './pages/AddVehicle.js'
import MyVehicles from './pages/MyVehicles.js'
import EditVehicle from './pages/EditVehicle.js'
import { ThemeProvider, useTheme } from './ThemeContext'
import { AuthProvider, useAuth } from './contexts/AuthContext'
import { AnimatedThemeToggler } from './Components/themeToggler'
import { getFeaturedVehicles } from './services/vehicleService'
import { searchVehicles } from './services/searchService'
import VehicleCard from './Components/VehicleCard'
import Pagination from './Components/Pagination'

// Component principal de la pàgina d'inici
function HomePage() {
  // Estat per gestionar la visibilitat del menú de perfil (true o false)
  const [showMenu, setShowMenu] = useState(false)
  // Utilitzar el context del tema
  const { isDarkMode, toggleTheme } = useTheme()
  // Utilitzar el context d'autenticació
  const { user, signOut } = useAuth()
  const navigate = useNavigate()
  // Estats per al formulari
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    message: '',
  })

  const [featuredVehicles, setFeaturedVehicles] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [currentPage, setCurrentPage] = useState(1)
  const [searchResults, setSearchResults] = useState(null)
  const [isSearching, setIsSearching] = useState(false)

  // Constants de paginació
  const VEHICLES_PER_CAROUSEL = 5
  const CAROUSELS_PER_PAGE = 5
  const VEHICLES_PER_PAGE = VEHICLES_PER_CAROUSEL * CAROUSELS_PER_PAGE // 25 vehicles per pàgina

  // Tancar menú en fer clic fora
  useEffect(() => {
    const handleClickOutside = event => {
      if (showMenu && !event.target.closest('.contenidorPerfil')) {
        setShowMenu(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [showMenu])

  // Carregar vehicles destacats quan es munta el component
  useEffect(() => {
    const loadFeaturedVehicles = async () => {
      try {
        setLoading(true)
        const vehicles = await getFeaturedVehicles(100) // Carregar més vehicles per a la paginació
        setFeaturedVehicles(vehicles)
      } catch (err) {
        setError('Error en carregar vehicles destacats')
        console.error(err)
      } finally {
        setLoading(false)
      }
    }

    loadFeaturedVehicles()
  }, [])

  // Calcular total de pàgines
  const vehiclesToShow = searchResults !== null ? searchResults : featuredVehicles
  const totalPages = Math.ceil(vehiclesToShow.length / VEHICLES_PER_PAGE)

  // Obtenir vehicles de la pàgina actual
  const getCurrentPageVehicles = () => {
    // Si hi ha cerca activa, mostrar resultats de cerca
    const vehiclesToShow = searchResults !== null ? searchResults : featuredVehicles

    const startIndex = (currentPage - 1) * VEHICLES_PER_PAGE
    const endIndex = startIndex + VEHICLES_PER_PAGE
    return vehiclesToShow.slice(startIndex, endIndex)
  }

  // Funció per gestionar la cerca
  const handleSearch = async searchTerm => {
    try {
      setIsSearching(true)
      setLoading(true)

      // Cercar en tots els vehicles (comprar + alquilar)
      const results = await searchVehicles(searchTerm, 'all')
      setSearchResults(results)
      setCurrentPage(1) // Reiniciar a primera pàgina

      setIsSearching(false)
      setLoading(false)
    } catch (err) {
      console.error('Error en cerca:', err)
      setError('Error en cercar vehicles')
      setIsSearching(false)
      setLoading(false)
    }
  }

  // Funció per netejar la cerca
  const handleClearSearch = () => {
    setSearchResults(null)
    setCurrentPage(1)
  }

  // Funció per canviar de pàgina
  const handlePageChange = newPage => {
    setCurrentPage(newPage)
    // Desplaçament suau a la secció de vehicles
    document.querySelector('.vehicles-section')?.scrollIntoView({ behavior: 'smooth' })
  }

  // Renderitzar carousels de la pàgina actual
  const renderCarousels = () => {
    const pageVehicles = getCurrentPageVehicles()
    const carousels = []

    for (let i = 0; i < CAROUSELS_PER_PAGE; i++) {
      const startIdx = i * VEHICLES_PER_CAROUSEL
      const endIdx = startIdx + VEHICLES_PER_CAROUSEL
      const carouselVehicles = pageVehicles.slice(startIdx, endIdx)

      if (carouselVehicles.length > 0) {
        const isReverse = i % 2 === 1 // Alternar direcció
        carousels.push(
          <div key={`carousel-${i}`} className='carousel-row'>
            <div className='carousel' mask='true' reverse={isReverse ? 'true' : undefined}>
              {carouselVehicles.map(vehicle => (
                <VehicleCard key={vehicle['vehicle_id(PK)']} vehicle={vehicle} />
              ))}
            </div>
          </div>
        )
      }
    }

    return carousels
  }

  // Funció per gestionar canvis als inputs del formulari
  const handleInputChange = e => {
    const { name, value } = e.target
    setFormData({
      ...formData,
      [name]: value,
    })
  }

  // Funció per gestionar l'enviament del formulari
  const handleSubmit = e => {
    e.preventDefault()
    // Aquí aniria la lògica per enviar les dades
    console.log('Dades del formulari:', formData)
    alert('Formulari enviat amb èxit!')
  }

  return (
    <div className='App'>
      <img src={mclovin} className='mclovin-logo' alt='imatge_mclovin' />
      {/* Icona del perfil a dalt a la dreta */}
      <div className='contenidorPerfil'>
        <div className='desplegablePerfil'>
          <img src={logo} className='logo-usuari' alt='usuari_logo' onClick={() => setShowMenu(!showMenu)} style={{ cursor: 'pointer' }} />
          {showMenu && (
            <div className='menuPerfil'>
              <p>Perfil</p>
              <p>Configuración</p>
              <p>Idioma</p>
              {user && (
                <p
                  onClick={() => {
                    navigate('/mis-vehiculos')
                    setShowMenu(false)
                  }}
                  style={{ cursor: 'pointer', color: '#61dafb', fontWeight: 'bold' }}
                >
                  Mis Vehículos
                </p>
              )}
              <p
                onClick={async () => {
                  console.log('CLICK en Tancar sessió')
                  setShowMenu(false)

                  // Esperar a que signOut acabi
                  const result = await signOut()
                  console.log('Resultat de signOut:', result)

                  // Redirigir
                  console.log('Redirigint a /login...')
                  window.location.href = '/login'
                }}
                style={{ cursor: 'pointer', color: '#61dafb', fontWeight: 'bold' }}
              >
                Cerrar sesión
              </p>
              <p>Ajuda</p>
              <div style={{ display: 'block', alignItems: 'center', gap: '10px', padding: '10px 0' }}>
                <AnimatedThemeToggler />
              </div>
            </div>
          )}
        </div>
      </div>
      <header className='appCabecera'>
        <h1 className='titulPrincipal' onClick={() => navigate('/')} style={{ cursor: 'pointer' }}>
          MotorVault
        </h1>
        {/* Barra de menús sota el títol */}
        <div className='barra-menus'>
          <Link to='/' className='menu-btn'>
            Inicio
          </Link>
          <Link to='/comprar' className='menu-btn'>
            Comprar
          </Link>
          <Link to='/alquilar' className='menu-btn'>
            Alquilar
          </Link>
          {user && (
            <Link to='/add-vehicle' className='menu-btn add-vehicle-btn' title='Añadir vehículo'>
              +
            </Link>
          )}
        </div>
        <SearchBar onSearch={handleSearch} onClear={handleClearSearch} placeholder='Buscar vehículos (marca, modelo, año...)' />
      </header>

      {/* Secció nova per al vídeo */}
      <div className='video-section'>
        <video className='video-player' autoPlay loop muted playsInline>
          <source src={NewVideo} type='video/mp4' />
          El teu navegador no suporta l'element de vídeo.
        </video>
      </div>

      {/* Secció de vehicles destacats */}
      <div className='vehicles-section'>
        <h2 className='section-title' style={{ color: '#61dafb' }}>
          {searchResults !== null ? `Resultados de búsqueda (${searchResults.length})` : 'Vehículos Destacados'}
        </h2>

        {loading && <p>Cargando vehículos...</p>}
        {error && <p style={{ color: 'red' }}>{error}</p>}

        {!loading && !error && searchResults !== null && searchResults.length === 0 && (
          <div style={{ textAlign: 'center', padding: '40px', color: '#888' }}>
            <p style={{ fontSize: '20px' }}>😕 No se encontraron vehículos</p>
            <p>Intenta con otros términos de búsqueda</p>
          </div>
        )}

        {!loading && !error && vehiclesToShow.length > 0 && (
          <>
            {/* Renderizar carousels de la página actual */}
            {renderCarousels()}
          </>
        )}
      </div>

      {/* Paginación - Entre carousels y formulario */}
      {!loading && !error && totalPages > 1 && <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={handlePageChange} />}

      {/* Secció de contacte similar a la imatge */}
      <div className='contact-section-wrapper'>
        <div className='contact-section'>
          <form className='contact-form' onSubmit={handleSubmit}>
            <div className='form-group'>
              <label htmlFor='firstName'>Nom (*)</label>
              <div className='name-fields'>
                <input type='text' id='firstName' name='firstName' placeholder='Nom' value={formData.firstName} onChange={handleInputChange} required />
                <input type='text' id='lastName' name='lastName' placeholder='Cognom' value={formData.lastName} onChange={handleInputChange} required />
              </div>
            </div>

            <div className='form-group'>
              <label htmlFor='email'>Email (*)</label>
              <input type='email' id='email' name='email' value={formData.email} onChange={handleInputChange} required />
            </div>

            <div className='form-group'>
              <label htmlFor='phone'>Telefono (*)</label>
              <div className='phone-input-container'>
                {' '}
                <div className='phone-prefix'>
                  <select name='phonePrefix' defaultValue='+34' onChange={handleInputChange} className='prefix-select'>
                    {countryCodes
                      .sort((a, b) => a.name.localeCompare(b.name)) // Ordenar alfabèticament per nom
                      .map(country => (
                        <option key={country.code} value={country.dial_code}>
                          {country.dial_code} ({country.name})
                        </option>
                      ))}
                  </select>
                </div>
                <span className='prefix-separator'>|</span>
                <input type='tel' id='phone' name='phone' value={formData.phone} onChange={handleInputChange} required />
              </div>
            </div>

            <div className='form-group'>
              <label htmlFor='message'>Missatge (*)</label>
              <textarea
                className='message-textarea'
                id='message'
                name='message'
                placeholder='Lorem Ipsum es simplemente el texto de relleno de las imprentas y archivos de texto. Lorem Ipsum ha sido el texto de relleno estándar de las industrias desde el año 1500'
                value={formData.message}
                onChange={handleInputChange}
                required
              ></textarea>
            </div>

            <button type='submit' className='submit-btn'>
              Enviar
            </button>
          </form>

          <div className='business-hours'>
            <h4>Horario de atención</h4>
            <p>Lun: 10:00 - 16:00</p>
            <p>Mar: 10:00 - 16:00</p>
            <p>Mié: 10:00 - 16:00</p>
            <p>Jue: 10:00 - 16:00</p>
            <p>Vie y Sáb: Con cita previa</p>
            <p>Dom: Cerrado</p>
          </div>

          <div className='contact-header'>
            <h2>Campredo</h2>
            <h3>Campredo City</h3>
            <div className='location-details'></div>
          </div>

          <div className='contact-info'>
            <iframe
              className='campredo-map'
              src={`https://maps.google.com/maps?q=${40.760702},${0.549335}&z=15&output=embed`}
              title='Unique title for iframe'
              width='600'
              height='450'
              style={{ border: 0 }}
              allowFullScreen=''
              loading='lazy'
              referrerPolicy='no-referrer-when-downgrade'
            ></iframe>

            <div className='contact-persons'>
              <p>
                <span>Gonofredo Piterson IV</span> - gonopit@motorvault.com / 677766676
              </p>
              <p>
                <span>Aurelio Correcaminos</span> - aurcormicmic@motorvault.com / 666777676
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

function App() {
  return (
    <AuthProvider>
      <ThemeProvider>
        <Router>
          <Routes>
            <Route path='/login' element={<Login />} />
            <Route path='/' element={<HomePage />} />
            <Route path='/vehicle/:id' element={<VehicleDetail />} />
            <Route path='/comprar' element={<Compra />} />
            <Route path='/alquilar' element={<Alquilar />} />
            <Route path='/add-vehicle' element={<AddVehicle />} />
            <Route path='/mis-vehiculos' element={<MyVehicles />} />
            <Route path='/edit-vehicle/:vehicleId' element={<EditVehicle />} />
          </Routes>
        </Router>
      </ThemeProvider>
    </AuthProvider>
  )
}

export default App
